import React, { useState, useEffect, useMemo } from 'react';
import { 
  TrendingUp, 
  ShoppingBag, 
  DollarSign, 
  Package, 
  AlertTriangle, 
  Download, 
  Shield
} from 'lucide-react';
import NavBar from '../../components/layout/NavBar';
import Footer from '../../components/layout/Footer';
import LojaService from '../../services/Loja/LojaService';
import { INITIAL_PRODUCTS } from '../../services/Loja/LojaMockData';

export default function LojaAdminView() {
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' | 'produtos' | 'estoque' | 'pedidos'
  const [kpis, setKpis] = useState(null);
  const [products, setProducts] = useState(INITIAL_PRODUCTS);
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrderStatus, setSelectedOrderStatus] = useState('todos');

  // Carrega KPIs e pedidos locais
  useEffect(() => {
    async function loadData() {
      const kpisData = await LojaService.getAdminKpis();
      setKpis(kpisData);

      try {
        const saved = JSON.parse(localStorage.getItem('urb_orders') || '[]');
        setOrders(saved);
      } catch (e) {}
    }
    loadData();
  }, []);

  // Consolidação de todas as variantes para gestão rápida de estoque
  const allVariants = useMemo(() => {
    const list = [];
    products.forEach(p => {
      (p.variants || []).forEach(v => {
        list.push({
          productId: p.id,
          productName: p.name,
          sku: v.sku || `${p.sku}-${v.size}`,
          size: v.size,
          color: v.color,
          gender: v.gender || p.gender,
          stock: v.stock
        });
      });
    });
    return list;
  }, [products]);

  // Atualização rápida de estoque em 1 clique
  const handleUpdateStock = (sku, delta) => {
    setProducts(prev => {
      return prev.map(p => {
        const hasVariant = (p.variants || []).some(v => v.sku === sku || `${p.sku}-${v.size}` === sku);
        if (!hasVariant) return p;

        return {
          ...p,
          variants: p.variants.map(v => {
            if (v.sku === sku || `${p.sku}-${v.size}` === sku) {
              const nextStock = Math.max(0, v.stock + delta);
              return { ...v, stock: nextStock };
            }
            return v;
          })
        };
      });
    });
  };

  // Alteração de status do pedido e código de rastreio
  const handleUpdateOrderStatus = (orderNumber, newStatus, trackingCode = null) => {
    setOrders(prev => {
      const updated = prev.map(o => {
        if (o.orderNumber === orderNumber) {
          return {
            ...o,
            status: newStatus,
            trackingCode: trackingCode || o.trackingCode
          };
        }
        return o;
      });
      try {
        localStorage.setItem('urb_orders', JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  // Exportação CSV formatado para Microsoft Excel (UTF-8 com BOM e delimitador ';')
  const handleExportCsv = () => {
    const headers = ['Numero_Pedido', 'Data', 'Cliente', 'CPF', 'Email', 'Telefone', 'Status', 'Metodo_Pagamento', 'Valor_Total', 'Rastreamento'];
    const rows = orders.map(o => [
      o.orderNumber,
      new Date(o.createdAt).toLocaleDateString('pt-BR'),
      `"${o.customer?.name || ''}"`,
      `"${o.customer?.cpf || ''}"`,
      o.customer?.email || '',
      `"${o.customer?.phone || ''}"`,
      o.status,
      o.paymentMethod,
      (o.totalAmount || 0).toFixed(2).replace('.', ','),
      o.trackingCode || ''
    ]);

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio_pedidos_loja_urb_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredOrders = orders.filter(o => {
    const matchStatus = selectedOrderStatus === 'todos' || o.status === selectedOrderStatus;
    const matchSearch = !searchTerm || 
      o.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (o.customer?.name && o.customer.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchStatus && matchSearch;
  });

  return (
    <div className="min-h-screen bg-urb-dark flex flex-col selection:bg-urb-primary selection:text-black">
      <NavBar />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-8">
        
        {/* Cabeçalho do Painel Admin */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-urb-border">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-urb-primary/10 border border-urb-primary/30 text-urb-primary text-xs font-black uppercase mb-1">
              <Shield className="w-3.5 h-3.5" />
              <span>PAINEL OPERACIONAL // LOJA OFICIAL</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight uppercase">
              Gestão Executiva Loja URB
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-urb-card-light hover:bg-urb-card border border-urb-border text-xs font-bold text-white uppercase tracking-wider transition-all cursor-pointer"
            >
              <Download className="w-4 h-4 text-urb-primary" />
              <span>Exportar Pedidos (CSV)</span>
            </button>
          </div>
        </div>

        {/* 6 KPIs em Tempo Real */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4">
          {[
            { label: 'Vendas Hoje', val: `R$ ${(kpis?.vendasHoje || 3840).toFixed(2).replace('.', ',')}`, icon: DollarSign, color: 'text-urb-primary' },
            { label: 'Vendas no Mês', val: `R$ ${(kpis?.vendasMes || 48920).toFixed(2).replace('.', ',')}`, icon: TrendingUp, color: 'text-urb-primary' },
            { label: 'Total Pedidos', val: kpis?.totalPedidos || orders.length || 142, icon: ShoppingBag, color: 'text-white' },
            { label: 'Ticket Médio', val: `R$ ${(kpis?.ticketMedio || 344.50).toFixed(2).replace('.', ',')}`, icon: DollarSign, color: 'text-white' },
            { label: 'Peças Vendidas', val: kpis?.pecasVendidas || 389, icon: Package, color: 'text-white' },
            { label: 'Estoque Baixo', val: `${kpis?.estoqueBaixoAlertas || 3} alertas`, icon: AlertTriangle, color: 'text-yellow-400' }
          ].map((kpi, idx) => {
            const Icon = kpi.icon;
            return (
              <div key={idx} className="p-4 rounded-2xl bg-urb-card border border-urb-border space-y-2">
                <div className="flex items-center justify-between text-urb-text-muted">
                  <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-wider">{kpi.label}</span>
                  <Icon className={`w-4 h-4 ${kpi.color}`} />
                </div>
                <div className={`text-base sm:text-lg font-black ${kpi.color}`}>
                  {kpi.val}
                </div>
              </div>
            );
          })}
        </div>

        {/* Abas do Painel */}
        <div className="flex items-center gap-2 border-b border-urb-border pb-2 overflow-x-auto no-scrollbar">
          {[
            { id: 'dashboard', label: 'Dashboard & Visão Geral' },
            { id: 'estoque', label: `Estoque por Variante (${allVariants.length})` },
            { id: 'pedidos', label: `Pedidos & Rastreio (${orders.length})` },
            { id: 'produtos', label: `Catálogo de Produtos (${products.length})` }
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                activeTab === tab.id
                  ? 'bg-urb-primary text-black shadow-[0_0_12px_rgba(138,230,0,0.3)]'
                  : 'bg-urb-card border border-urb-border text-urb-text-secondary hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* CONTEÚDO DAS ABAS */}

        {/* ABA: ESTOQUE POR VARIANTE */}
        {activeTab === 'estoque' && (
          <div className="rounded-3xl bg-urb-card border border-urb-border overflow-hidden space-y-4 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-extrabold text-white uppercase">
                  Ajuste Rápido de Estoque em 1 Clique
                </h3>
                <p className="text-xs text-urb-text-secondary mt-0.5">
                  Atualize o estoque das variantes individualmente. O status na loja virtual reflete imediatamente.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-urb-text-secondary">
                <thead className="text-[11px] font-black uppercase text-urb-text-muted border-b border-urb-border bg-urb-dark/50">
                  <tr>
                    <th className="py-3 px-4">Produto</th>
                    <th className="py-3 px-4">SKU</th>
                    <th className="py-3 px-4">Tamanho</th>
                    <th className="py-3 px-4">Cor</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-center">Estoque Atual</th>
                    <th className="py-3 px-4 text-right">Ajuste Rápido</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-urb-border/50">
                  {allVariants.map((item, idx) => {
                    const isZero = item.stock === 0;
                    const isLow = item.stock > 0 && item.stock <= 5;

                    return (
                      <tr key={idx} className="hover:bg-urb-card-light/50 transition-colors">
                        <td className="py-3.5 px-4 font-bold text-white max-w-xs truncate">
                          {item.productName}
                        </td>
                        <td className="py-3.5 px-4 font-mono text-[11px] text-urb-text-muted">
                          {item.sku}
                        </td>
                        <td className="py-3.5 px-4 font-black text-white">
                          {item.size}
                        </td>
                        <td className="py-3.5 px-4">
                          {item.color}
                        </td>
                        <td className="py-3.5 px-4">
                          {isZero ? (
                            <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-black text-[10px] uppercase">
                              Esgotado
                            </span>
                          ) : isLow ? (
                            <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-black text-[10px] uppercase">
                              Baixo ({item.stock})
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-urb-primary/20 text-urb-primary font-black text-[10px] uppercase">
                              Normal
                            </span>
                          )}
                        </td>
                        <td className="py-3.5 px-4 text-center font-black text-sm text-white">
                          {item.stock}
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <div className="inline-flex items-center gap-1 bg-urb-dark rounded-lg p-0.5 border border-urb-border">
                            <button
                              type="button"
                              onClick={() => handleUpdateStock(item.sku, -1)}
                              disabled={item.stock <= 0}
                              className="w-7 h-7 rounded flex items-center justify-center text-white hover:bg-white/10 disabled:opacity-20 cursor-pointer"
                            >
                              -1
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateStock(item.sku, +1)}
                              className="w-7 h-7 rounded flex items-center justify-center text-urb-primary hover:bg-urb-primary/10 font-bold cursor-pointer"
                            >
                              +1
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateStock(item.sku, +5)}
                              className="px-2 h-7 rounded flex items-center justify-center text-xs text-white hover:bg-white/10 font-bold cursor-pointer"
                            >
                              +5
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ABA: PEDIDOS & RASTREIO */}
        {activeTab === 'pedidos' && (
          <div className="rounded-3xl bg-urb-card border border-urb-border p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-base font-extrabold text-white uppercase">
                  Gestão de Pedidos
                </h3>
                <p className="text-xs text-urb-text-secondary mt-0.5">
                  Acompanhe transações, altere status e insira códigos de rastreio dos Correios/Transportadora.
                </p>
              </div>

              {/* Filtros de Pedidos */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por cliente ou código..."
                  className="px-3.5 py-2 rounded-xl bg-urb-dark border border-urb-border text-xs text-white focus:outline-none focus:border-urb-primary"
                />
                <select
                  value={selectedOrderStatus}
                  onChange={(e) => setSelectedOrderStatus(e.target.value)}
                  className="px-3.5 py-2 rounded-xl bg-urb-dark border border-urb-border text-xs text-white focus:outline-none focus:border-urb-primary cursor-pointer"
                >
                  <option value="todos">Todos os Status</option>
                  <option value="aguardando_pagamento">Aguardando Pagamento</option>
                  <option value="pagamento_aprovado">Pagamento Aprovado</option>
                  <option value="em_separacao">Em Separação</option>
                  <option value="pronto_para_envio">Pronto para Envio</option>
                  <option value="enviado">Enviado</option>
                  <option value="entregue">Entregue</option>
                </select>
              </div>
            </div>

            {filteredOrders.length === 0 ? (
              <div className="p-8 text-center text-xs text-urb-text-muted">
                Nenhum pedido encontrado para os filtros selecionados.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredOrders.map((ord) => (
                  <div key={ord.orderNumber} className="p-4 rounded-2xl bg-urb-dark border border-urb-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-white text-sm">{ord.orderNumber}</span>
                        <span className="px-2 py-0.5 rounded-full bg-urb-primary/10 border border-urb-primary/30 text-urb-primary text-[10px] font-black uppercase">
                          {ord.status.replace(/_/g, ' ')}
                        </span>
                        <span className="text-xs text-urb-text-muted">
                          • {new Date(ord.createdAt).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <p className="text-xs text-urb-text-secondary">
                        Cliente: <strong className="text-white">{ord.customer?.name}</strong> • WhatsApp: {ord.customer?.phone}
                      </p>
                      <p className="text-xs text-urb-text-muted">
                        {ord.items?.length || 1} itens • Total: <strong className="text-urb-primary">R$ {(ord.totalAmount || 0).toFixed(2).replace('.', ',')}</strong> ({ord.paymentMethod})
                      </p>
                    </div>

                    {/* Ações do Pedido */}
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={ord.status}
                        onChange={(e) => handleUpdateOrderStatus(ord.orderNumber, e.target.value)}
                        className="px-3 py-1.5 rounded-lg bg-urb-card border border-urb-border text-xs text-white focus:outline-none focus:border-urb-primary cursor-pointer"
                      >
                        <option value="aguardando_pagamento">Aguardando Pagamento</option>
                        <option value="pagamento_aprovado">Pagamento Aprovado</option>
                        <option value="em_separacao">Em Separação</option>
                        <option value="pronto_para_envio">Pronto para Envio</option>
                        <option value="enviado">Enviado</option>
                        <option value="entregue">Entregue</option>
                      </select>

                      <input
                        type="text"
                        defaultValue={ord.trackingCode || ''}
                        onBlur={(e) => handleUpdateOrderStatus(ord.orderNumber, ord.status, e.target.value)}
                        placeholder="Rastreio (ex: BR123456789)"
                        className="w-40 px-3 py-1.5 rounded-lg bg-urb-card border border-urb-border text-xs text-white placeholder-urb-text-muted font-mono"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ABA: PRODUTOS & CATÁLOGO */}
        {(activeTab === 'dashboard' || activeTab === 'produtos') && (
          <div className="rounded-3xl bg-urb-card border border-urb-border p-6 space-y-4">
            <h3 className="text-base font-extrabold text-white uppercase">
              Catálogo de Produtos em Operação
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {products.map(p => (
                <div key={p.id} className="p-4 rounded-2xl bg-urb-dark border border-urb-border flex gap-3.5 items-center">
                  <div className="w-14 h-14 rounded-xl bg-urb-card p-1 shrink-0 flex items-center justify-center">
                    <img 
                      src={(p.images && p.images[0]?.url) || p.images[0] || '/images/produtos/camisa-urb.png'} 
                      alt={p.name} 
                      className="w-full h-full object-contain" 
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs font-bold text-white truncate">{p.name}</h4>
                    <span className="text-xs font-black text-urb-primary block">
                      R$ {(p.promotional_price || p.price).toFixed(2).replace('.', ',')}
                    </span>
                    <span className="text-[10px] text-urb-text-muted">
                      {p.variants?.length || 0} variantes cadastradas
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </main>

      <Footer />
    </div>
  );
}
